import axios from 'axios'
import { ctrDecrypt, newEnvelope } from './cryptoEnvelope'
import { sealStorage } from '../common-misc/cryptoStorage'

// The browser never holds the master key. Each login gives it an opaque credential (sent as the
// `credential` header) and the session key `sk` that encrypts bodies, responses, URLs and localStorage.
// See msp_backend_authentication/docs/SESSION-KEY-ENVELOPE.md.
export const SESSION_KEY_STORAGE = 'mspSk'
export const SESSION_CREDENTIAL_STORAGE = 'mspSkCredential'
export const VSPACE_CREDENTIAL_STORAGE = 'dataVspace'
export const PORTAL_CREDENTIAL_STORAGE = 'mspCredential'
export const LOGIN_INFO_STORAGE = 'loginInfo'
export const ENVELOPE_HEADER = 'X-Encrypt-Key'
export const EXCHANGE_PATH = '/api/session/exchange'

export interface IssuedSession {
    credential: string
    sk: string
}

export type SessionKind = 'vspace' | 'portal'

export interface LoginSessionResult {
    credential?: string
    sk?: string
    email?: string
    roleId?: number | string
    token?: string
}

function tabStorage(): Storage | null {
    try {
        return window.sessionStorage
    } catch (e) {
        return null
    }
}

// The credential header of every goservice request: the Vspace credential, or the MSP portal one.
export function storedCredential(): string {
    return localStorage.getItem(VSPACE_CREDENTIAL_STORAGE) || localStorage.getItem(PORTAL_CREDENTIAL_STORAGE) || ''
}

// This tab's session key, only while it still belongs to the stored credential: when another tab logs
// in, this tab must exchange the new credential instead of sending it with the old key.
export function getSessionKey(): Buffer | null {
    const store = tabStorage()
    if (!store) return null
    const sk = store.getItem(SESSION_KEY_STORAGE)
    const credential = storedCredential()
    if (!sk || !credential || store.getItem(SESSION_CREDENTIAL_STORAGE) !== credential) return null
    const key = Buffer.from(sk, 'base64')
    return key.length === 32 ? key : null
}

export function saveSession(issued: IssuedSession, kind: SessionKind) {
    const store = tabStorage()
    if (!store) throw new Error('sessionStorage is not available')
    if (kind === 'vspace') {
        localStorage.setItem(VSPACE_CREDENTIAL_STORAGE, issued.credential)
        localStorage.removeItem(PORTAL_CREDENTIAL_STORAGE)
    } else {
        localStorage.setItem(PORTAL_CREDENTIAL_STORAGE, issued.credential)
        localStorage.removeItem(VSPACE_CREDENTIAL_STORAGE)
        localStorage.removeItem(LOGIN_INFO_STORAGE)
    }
    store.setItem(SESSION_KEY_STORAGE, issued.sk)
    store.setItem(SESSION_CREDENTIAL_STORAGE, issued.credential)
    // Values written before login are still plaintext: encrypt them now that there is a key.
    sealStorage(window)
}

function issued(result: LoginSessionResult): IssuedSession {
    if (!result.credential || !result.sk) throw new Error('Login response has no session credential')
    return { credential: result.credential, sk: result.sk }
}

// SSO, /vSpaceLogin and a Vspace credential exchange: also keep the login details decryptDataVspace() reads.
export function saveVspaceLogin(result: LoginSessionResult) {
    saveSession(issued(result), 'vspace')
    localStorage.setItem(LOGIN_INFO_STORAGE, JSON.stringify({ email: result.email, roleId: Number(result.roleId), token: result.token }))
}

export function savePortalLogin(result: LoginSessionResult) {
    saveSession(issued(result), 'portal')
}

export function clearSession() {
    localStorage.removeItem(VSPACE_CREDENTIAL_STORAGE)
    localStorage.removeItem(PORTAL_CREDENTIAL_STORAGE)
    localStorage.removeItem(LOGIN_INFO_STORAGE)
    const store = tabStorage()
    if (store) {
        store.removeItem(SESSION_KEY_STORAGE)
        store.removeItem(SESSION_CREDENTIAL_STORAGE)
    }
}

// The goservice backends with CryptoKeyFilter and the encrypt/decrypt advices (asset, km, auth, wo).
export function isAdviceBackend(baseURL?: string): boolean {
    const advice = [
        process.env.REACT_APP_BASE_URL_ASSET_MANAGEMENT,
        process.env.REACT_APP_BASE_URL_KNOWLEDGE_MANAGEMENT,
        process.env.REACT_APP_BASE_URL_AUTH,
        process.env.REACT_APP_BASE_URL_WO_MANAGEMENT
    ]
    return !!baseURL && advice.indexOf(baseURL) !== -1
}

let pendingExchange: Promise<void> | null = null

// Trade the stored credential (legacy from Vspace or mobile, or a v2 from another tab) for this tab's
// session key. Concurrent callers share one request.
export function exchangeSession(): Promise<void> {
    if (!pendingExchange) {
        pendingExchange = runExchange().then(
            () => { pendingExchange = null },
            (e) => {
                pendingExchange = null
                throw e
            })
    }
    return pendingExchange
}

async function runExchange() {
    const credential = storedCredential()
    if (!credential) throw new Error('No credential to exchange')
    const kind: SessionKind = localStorage.getItem(VSPACE_CREDENTIAL_STORAGE) ? 'vspace' : 'portal'
    const envelope = newEnvelope()
    try {
        const response = await axios.post(process.env.REACT_APP_BASE_URL_AUTH + EXCHANGE_PATH, null, {
            headers: { credential, [ENVELOPE_HEADER]: envelope.header, 'X-Encrypt-Response': '1' }
        })
        // Another tab logged in meanwhile: its session wins, the caller re-evaluates.
        if (storedCredential() !== credential) return
        const data = JSON.parse(ctrDecrypt(envelope.key, response.data.data))
        if (kind === 'vspace') saveVspaceLogin(data)
        else savePortalLogin(data)
    } catch (e) {
        if (e && e.response && e.response.status === 401 && storedCredential() === credential) clearSession()
        throw e
    }
}

// First request interceptor (registered last in axios.ts; axios runs request interceptors in reverse):
// choose this request's AES key and keep it on the config for the URL, body and response interceptors.
export async function resolveRequestKey(config: any) {
    if (!isAdviceBackend(config.baseURL)) return config
    if (!config.headers) config.headers = {}
    if (!getSessionKey() && storedCredential()) {
        try {
            await exchangeSession()
        } catch (e) {
            // No session: auth still gets an envelope below, everything else is refused.
        }
    }
    const sk = getSessionKey()
    if (sk) {
        config.cryptoKey = sk
        config.headers.credential = storedCredential()
        return config
    }
    if (config.baseURL === process.env.REACT_APP_BASE_URL_AUTH) {
        const envelope = newEnvelope()
        config.cryptoKey = envelope.key
        config.headers[ENVELOPE_HEADER] = envelope.header
        return config
    }
    // Shaped like an axios 401, so existing handlers that read err.response.status / .data keep working.
    const error: any = new Error('No session key for ' + config.baseURL + ': log in again')
    error.response = { status: 401, data: { message: 'Session expired. Please log in again.' } }
    throw error
}
